import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import "./PublicAdReport.scss";
import DynamicHeader from "../../Header/DynamicHeader";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

async function fetchJson(url, options) {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...(options || {}),
  });
  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const msg = (data && data.error) || (data && data.message) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDayUk(dayKey) {
  if (!dayKey || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return dayKey || "";
  const d = new Date(`${dayKey}T00:00:00`);
  try {
    return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
  }
}

function formatPeriodUk(from, to) {
  if (!from || !to) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return `${from} — ${to}`;
  if (from === to) return formatDayUk(from);
  return `${formatDayUk(from)} — ${formatDayUk(to)}`;
}

function formatNumber(n) {
  const x = Number(n || 0);
  try {
    return x.toLocaleString("uk-UA");
  } catch {
    return String(x);
  }
}

function formatExpiresAt(expiresAtSec) {
  const sec = Number(expiresAtSec || 0);
  if (!sec) return "";
  const d = new Date(sec * 1000);
  try {
    return d.toLocaleString("uk-UA", { year: "numeric", month: "long", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
}

export default function PublicAdReport() {
  const { token } = useParams();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!token) {
      setErr("Невірне посилання.");
      setData(null);
      return;
    }

    setErr("");
    setLoading(true);
    try {
      const out = await fetchJson(`${API_BASE}/api/ads/report-share/${encodeURIComponent(token)}`);
      setData(out || null);
    } catch (e) {
      setErr(String(e.message || e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || null;
  const days = data?.days || [];
  const period = useMemo(() => formatPeriodUk(data?.from, data?.to), [data?.from, data?.to]);

  const ctrTotal = useMemo(() => {
    const impr = Number(totals?.impressions || 0);
    const clk = Number(totals?.clicks || 0);
    if (!impr) return 0;
    return (clk / impr) * 100;
  }, [totals]);

  return (
    <>
      <DynamicHeader
        title="Звіт реклами"
        variant="glass"
        size="regular"
        hideOnScroll={false}
        showBack={true}
        showMenu={false}
        withSpacer={false}
      />
      <div className="local-header-spacer" />

      <div className="cv-report-wrap">
        <div className="cv-report-card">
          <div className="cv-report-top">
            <div className="cv-report-h">
              <div className="cv-report-title">Звіт рекламної кампанії</div>
              <div className="cv-report-sub">
                {period ? <span className="pill">Період: {period}</span> : null}
                {data?.expiresAt ? <span className="pill warn">Дійсне до: {formatExpiresAt(data.expiresAt)}</span> : null}
              </div>
            </div>

            <div className="cv-report-actions">
              <button className="btn" disabled={loading} onClick={load}>
                {loading ? "Оновлення..." : "Оновити"}
              </button>
              <Link className="btn secondary" to="/">
                На головну
              </Link>
            </div>
          </div>

          {err ? (
            <div className="cv-report-error">
              <div className="e-title">Не вийшло завантажити звіт</div>
              <div className="e-msg">{err}</div>
              <div className="e-hint">Якщо посилання прострочене — попросіть нове в кабінеті реклами.</div>
            </div>
          ) : null}

          {!err && !loading && data ? (
            <>
              <div className="cv-report-kpis">
                <div className="kpi">
                  <div className="kpi-label">Покази</div>
                  <div className="kpi-value">{formatNumber(totals?.impressions || 0)}</div>
                </div>
                <div className="kpi">
                  <div className="kpi-label">Кліки</div>
                  <div className="kpi-value">{formatNumber(totals?.clicks || 0)}</div>
                </div>
                <div className="kpi">
                  <div className="kpi-label">CTR</div>
                  <div className="kpi-value">{ctrTotal.toFixed(2)}%</div>
                </div>
              </div>

              <div className="cv-report-table">
                <div className="thead">
                  <div>Дата</div>
                  <div>Покази</div>
                  <div>Кліки</div>
                  <div>CTR</div>
                </div>

                {days && days.length ? (
                  days.map((d) => {
                    const impr = Number(d.impressions || 0);
                    const clk = Number(d.clicks || 0);
                    const c = impr ? (clk / impr) * 100 : 0;
                    return (
                      <div className="trow" key={d.day}>
                        <div className="day">{formatDayUk(d.day)}</div>
                        <div>{formatNumber(impr)}</div>
                        <div>{formatNumber(clk)}</div>
                        <div>{c.toFixed(2)}%</div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty">Немає даних за вибраний період.</div>
                )}
              </div>

              <div className="cv-report-footnote">
                Це публічний звіт. Посилання має обмежений строк дії та може бути відкликане.
              </div>
            </>
          ) : null}

          {!err && loading ? (
            <div className="cv-report-loading">
              <div className="spinner" />
              <div>Завантаження звіту…</div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
